import { useId, type ComponentProps } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '../../lib/utils.ts';
import { joinIds, lockText, type FieldLock } from './input.tsx';
import { fieldClass } from './variants.ts';

/**
 * Mehrzeiliges Textfeld (DESIGN U22 „Einheitliche Eingaben“, Ticket 1.215) im Aussehen des Formularfelds (`Input`,
 * `fieldClass`, Ticket 1.256): weiß mit klarem Rand, Fokus 2 px blauer Rand, Fehler 2 px rot; in der Höhe ziehbar.
 * `locked`: gesperrt ohne Fläche, Schloss oben rechts, „nicht änderbar“ für Hilfsmittel; `readOnly` allein ohne Schloss.
 * `autoGrow` (Ticket 1.260, z. B. Notiz am Kunden): startet mit 2 Zeilen und wächst mit dem Text (`field-sizing: content`;
 * höchstens `max-h-80`, danach scrollt das Feld; ohne Unterstützung im Browser bleiben 2 Zeilen, in der Höhe ziehbar).
 * Setzt ein Aufrufer die Höhe unter 44 px (z. B. schmale Notiz `min-h-10`), ergänzt er `pointer-coarse:min-h-11` (Ticket 296);
 * die Grundhöhen `min-h-24`/`min-h-17` liegen schon darüber.
 */
export function Textarea({
  className,
  invalid,
  locked,
  readOnly,
  autoGrow = false,
  'aria-describedby': describedBy,
  ...props
}: ComponentProps<'textarea'> & { invalid?: boolean; locked?: FieldLock | undefined; autoGrow?: boolean }) {
  const lockId = useId();
  const isLocked = !!locked || !!readOnly;
  const area = (
    <textarea
      aria-invalid={invalid || undefined}
      aria-describedby={joinIds(describedBy, locked ? lockId : undefined)}
      readOnly={isLocked || undefined}
      data-field-box={isLocked ? 'locked' : 'edit'}
      className={cn(
        fieldClass({ invalid, locked: isLocked }),
        'block w-full resize-y px-space-4 py-space-3 leading-[1.45]',
        autoGrow ? 'min-h-17 max-h-80 field-sizing-content' : 'min-h-24',
        locked ? 'pr-[var(--field-end-icon)]' : undefined,
        className,
      )}
      {...props}
    />
  );
  if (!locked) return area;
  return (
    <div className="relative">
      {area}
      <span className="pointer-events-none absolute top-3 right-4 flex items-center text-ink-2">
        <Lock size={16} strokeWidth={1.5} aria-hidden data-testid="feld-schloss" />
        <span id={lockId} className="sr-only">
          {lockText(locked)}
        </span>
      </span>
    </div>
  );
}
